import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@chainlit/react-client';

const ReactApexChart = lazy(() => import('react-apexcharts'));

import { Header } from '@/components/header';
import { SidebarProvider } from '@/components/ui/sidebar';
import AddWidgetDialog from '@/components/dashboard/AddWidgetDialog';
import FilterPanel from '@/components/dashboard/FilterPanel';
import Narrative, { type NarrativeData } from '@/components/dashboard/Narrative';
import DrilldownPanel, { type DrilldownTarget } from '@/components/dashboard/DrilldownPanel';
import HealthScoreRow from '@/components/dashboard/HealthScoreRow';
import { apiRemoveWidget, apiResetLayout, apiUpdateFilterVisibility, apiUpdateFilterOrder, apiResetFilters, apiAdminSetTenantWidgets, apiAdminSetTenantFilters, apiUpdateWidgetType, apiAdminUpdateTenantWidgetType } from '@/components/dashboard/api';
import { useFeatures } from '@/hooks/useFeatures';
import { type Filter, type ActiveFilters, type FilterValue, type DateRangeValue, buildFilterPayload, filterKey } from '@/components/dashboard/filterTypes';
import { DEFAULT_PERIOD, PERIOD_LABEL, PERIOD_ORDER, type PeriodId, periodFromFilters, periodRange, primaryDateFilter } from '@/components/dashboard/periods';

// ── widget types ──────────────────────────────────────────────────────────────

// `previous`/`delta_pct` arrive whenever a date range is active: a total with
// no reference point cannot drive a decision. direction is 'none' when the
// baseline is zero or missing — a percentage against zero is meaningless.
interface Comparison    { previous?: number | null; delta_pct?: number | null;
                          direction?: 'up' | 'down' | 'flat' | 'none' }
interface KpiWidget     { type: 'kpi';      table: string; title: string; value: number; unit: string }
interface CountWidget   { type: 'count';    table: string; title: string; value: number; unit: string }
interface LineWidget    { type: 'line';     table: string; title: string; x: string[]; y: number[] }
interface AreaWidget    { type: 'area';     table: string; title: string; x: string[]; y: number[] }
interface BarWidget     { type: 'bar';      table: string; title: string; labels: string[]; values: number[] }
interface TopNWidget    { type: 'top_n';    table: string; title: string; labels: string[]; values: number[] }
interface PieWidget     { type: 'pie';      table: string; title: string; labels: string[]; values: number[] }
interface CountByWidget { type: 'count_by'; table: string; title: string; labels: string[]; values: number[] }

// The backend returns this instead of a figure when the nightly data-sanity
// check flagged the column a widget reads: a blank with a reason beats a
// confident wrong number (sobhy's gross-profit trend was drawing a TOTAL_COGS
// that totals 33.7x its own sales).
export interface Unavailable { unavailable: true; reason: string; column?: string }

export type KpiLike   = (KpiWidget | CountWidget) & Comparison;
export type ChartOnly = LineWidget | AreaWidget | BarWidget | TopNWidget | PieWidget | CountByWidget;
// The backend tags each widget: 'executive' is what an owner reads, 'finance'
// is real work for an accountant (Debit by Account Group, Transaction Lines by
// Account Type) that was crowding the same screen. Nothing is deleted — the
// finance block simply lives behind its own tab.
export type WidgetGroup = 'executive' | 'finance';
export type Widget    = (KpiLike | ChartOnly) & Partial<Unavailable> & { group?: WidgetGroup };
type WidgetType = Widget['type'];

// What a widget may be switched TO. Narrower than the backend's compatibility
// rule on purpose: the two conversions it used to offer changed what the number
// MEANS rather than how it is drawn. Switching a KPI (SUM of a money column) to
// "Count" relabelled 5.4B as 5.4B *records*, and turning a measure into
// "Count by" claimed a total was a row count. A single-option entry hides the
// picker altogether.
const WIDGET_TYPE_GROUPS: Record<WidgetType, WidgetType[]> = {
  kpi:      ['kpi'],
  count:    ['count'],
  line:     ['line', 'area'],
  area:     ['line', 'area'],
  bar:      ['bar', 'top_n', 'pie'],
  top_n:    ['bar', 'top_n', 'pie'],
  pie:      ['bar', 'top_n', 'pie'],
  count_by: ['count_by', 'bar', 'top_n', 'pie'],
};

const WIDGET_TYPE_LABELS: Record<WidgetType, string> = {
  kpi: 'KPI', count: 'Count',
  line: 'Line', area: 'Area',
  bar: 'Bar', top_n: 'Top N', pie: 'Pie', count_by: 'Count by',
};

interface DashboardData {
  widgets: Widget[];
  filters: Filter[];
  filters_catalog?: Filter[];
  layout_source?: string;
  is_dashboard_admin?: boolean;
  narrative_available?: boolean;
  /** Filters the server applied that the page did not send: the opening period. */
  applied_filters?: ActiveFilters;
  /** Titles of widgets that failed to draw on this load (timeout, query error). */
  incomplete?: string[];
}

// ── apex theme sync ───────────────────────────────────────────────────────────

function useApexTheme() {
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

// ── shared apex defaults ──────────────────────────────────────────────────────

const PALETTE = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];

function baseOptions(isDark: boolean): ApexCharts.ApexOptions {
  const textColor = isDark ? '#94a3b8' : '#64748b';
  const gridColor = isDark ? '#1e293b' : '#f1f5f9';
  return {
    chart: {
      background: 'transparent',
      toolbar: { show: false },
      animations: { enabled: true, speed: 400 },
      fontFamily: 'inherit',
    },
    colors: PALETTE,
    theme: { mode: isDark ? 'dark' : 'light' },
    grid: { borderColor: gridColor, strokeDashArray: 4 },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      y: { formatter: (v: number) => formatCompact(v) },
    },
    dataLabels: { enabled: false },
    xaxis: {
      labels: {
        style: { colors: textColor, fontSize: '11px' },
        // Category names here are the customer's own (cost centres, work items,
        // projects) and are routinely long and near-identical at the start --
        // "... / Site Construction Works" four times over, all cut at the same
        // point, which reads as one repeated label. Trim from the END so the
        // part that differs survives, cap the rotated height so long names
        // cannot eat the plot, and drop labels rather than overlap them.
        trim: true,
        hideOverlappingLabels: true,
        maxHeight: 96,
        rotate: -35,
        rotateAlways: false,
        formatter: (v: string) =>
          typeof v === 'string' && v.length > 28 ? '…' + v.slice(-27) : v,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: textColor, fontSize: '11px' },
        formatter: (v: number) => formatCompact(v),
      },
    },
    legend: { labels: { colors: textColor }, fontSize: '11px' },
  };
}

// ── main page ────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { isAuthenticated, isReady: authReady } = useAuth();
  const navigate = useNavigate();
  const { features, loading: featuresLoading } = useFeatures();
  const [data, setData]           = useState<DashboardData | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({});
  const [editScope, setEditScope] = useState<'user' | 'admin' | null>(null);
  const [snapshot, setSnapshot]   = useState<DashboardData | null>(null);
  const [addOpen, setAddOpen]     = useState(false);
  const [resetting, setResetting] = useState(false);
  const [busyMsg, setBusyMsg]     = useState<string | null>(null);
  const editMode = editScope !== null;
  const busy = busyMsg !== null;
  const isDark = useApexTheme();
  const filtersRef = useRef<Filter[]>([]);
  // Derived, not stored: clearing the date from the filter bar has to move the
  // pill too, or the dashboard claims a period it is not showing.
  const period = periodFromFilters(data?.filters ?? [], activeFilters);
  const [group, setGroup] = useState<WidgetGroup>('executive');
  const [narrative, setNarrative] = useState<NarrativeData | null>(null);
  const [narrLoading, setNarrLoading] = useState(false);
  const [drilldown, setDrilldown] = useState<DrilldownTarget | null>(null);
  const periodApplied = useRef(false);

  const load = (
    af?: ActiveFilters,
    opts?: { scope?: 'tenant_default'; defaultRange?: DateRangeValue | null }
  ): Promise<void> => {
    if (!data) setLoading(true);
    setError(null);
    const payload = buildFilterPayload(filtersRef.current, af ?? activeFilters);
    const params = new URLSearchParams();
    if (payload.length > 0) params.set('active_filters', JSON.stringify(payload));
    if (opts?.scope) params.set('scope', opts.scope);
    // No value lists in this answer: each dropdown fetches its own when it
    // opens (FilterPanel useRemoteOptions). Inline they were 5.7 MB gzipped
    // on femex-new, sent and parsed again on every filter change.
    params.set('defer_options', '1');
    // The page does not know the tenant's date columns before its first
    // answer, so it sends the opening period as a bare range and the server
    // puts it on the right column. Without this the first visit ran every
    // widget over all of history, then again for the period.
    if (opts?.defaultRange) params.set('default_range', JSON.stringify(opts.defaultRange));
    const qs = params.toString();
    const url = qs ? `/api/dashboard/data?${qs}` : '/api/dashboard/data';
    return fetch(url)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d: DashboardData) => {
        filtersRef.current = d.filters;
        const applied =
          d.applied_filters && Object.keys(d.applied_filters).length > 0
            ? d.applied_filters
            : null;
        if (applied) {
          periodApplied.current = true;
          setActiveFilters({ ...(af ?? activeFilters), ...applied });
        }
        setData(d);
        setLoading(false);
        // Asked for separately so a model call can never delay the tiles; the
        // backend reuses the results of this very request instead of re-running
        // the SQL, and an admin previewing the company default is not the
        // audience for it.
        // The very first load runs before the default period is known, and is
        // immediately superseded by the period load — paying for a narrative of
        // a view the user never sees would double the cost of every visit.
        const supersededByPeriod =
          !periodApplied.current && !!primaryDateFilter(d.filters || []);
        if (d.narrative_available && !opts?.scope && !supersededByPeriod) {
          loadNarrative(
            applied
              ? buildFilterPayload(d.filters, { ...(af ?? activeFilters), ...applied })
              : payload
          );
        }
      })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  const loadNarrative = (payload: unknown[]) => {
    setNarrative(null);
    setNarrLoading(true);
    const params = new URLSearchParams();
    if (payload.length > 0) params.set('active_filters', JSON.stringify(payload));
    fetch(`/api/dashboard/narrative?${params.toString()}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => setNarrative(d?.narrative ?? null))
      .catch(() => setNarrative(null))
      .finally(() => setNarrLoading(false));
  };

  useEffect(() => {
    if (!authReady) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    if (!featuresLoading && features.dashboard) {
      if (periodApplied.current) load(activeFilters);
      else load({}, { defaultRange: periodRange(DEFAULT_PERIOD) });
    }
  }, [authReady, isAuthenticated, featuresLoading, features.dashboard]);

  // Fallback for when the server could not apply the opening period (a layout
  // being generated, or a tenant whose filter catalog is not stored yet): the
  // first response is what reveals the date columns, so apply it from here —
  // once; after that the user owns it.
  useEffect(() => {
    if (periodApplied.current || !data) return;
    const dateFilter = primaryDateFilter(data.filters || []);
    if (!dateFilter) { periodApplied.current = true; return; }
    periodApplied.current = true;
    const range = periodRange(DEFAULT_PERIOD);
    if (!range) return;
    const next = { ...activeFilters, [dateFilter.id]: range };
    setActiveFilters(next);
    load(next);
  }, [data]);

  const applyPeriod = (next: PeriodId) => {
    const dateFilter = primaryDateFilter(filtersRef.current);
    if (!dateFilter) return;
    const range = periodRange(next);
    const updated = { ...activeFilters };
    if (range) updated[dateFilter.id] = range;
    else delete updated[dateFilter.id];
    setActiveFilters(updated);
    load(updated);
  };

  const applyFilterKey = (key: string, val: FilterValue | null) => {
    const next = { ...activeFilters };
    if (val === null) delete next[key]; else next[key] = val;
    setActiveFilters(next);
    load(next);
  };

  const clearAll = () => { setActiveFilters({}); load({}); };

  const hasActive = Object.values(activeFilters).some(v => {
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object' && v !== null) {
      if ('from' in v || 'to' in v) return !!(v as { from?: string; to?: string }).from || !!(v as { from?: string; to?: string }).to;
      if ('q' in v) return !!(v as { q: string }).q;
      if ('from' in v || 'to' in v) return (v as { from?: number; to?: number }).from != null || (v as { from?: number; to?: number }).to != null;
    }
    return false;
  });

  const isAdmin = data?.is_dashboard_admin ?? false;

  const startEdit = async (scope: 'user' | 'admin') => {
    if (!data) return;
    if (scope === 'admin') {
      setBusyMsg('Loading company default…');
      try {
        const payload = buildFilterPayload(filtersRef.current, activeFilters);
        const params = new URLSearchParams({ scope: 'tenant_default' });
        if (payload.length > 0) params.set('active_filters', JSON.stringify(payload));
        const res = await fetch(`/api/dashboard/data?${params.toString()}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const tenantData: DashboardData = await res.json();
        filtersRef.current = tenantData.filters;
        setData(tenantData);
        setSnapshot(structuredClone(tenantData));
        setEditScope('admin');
      } catch (e) {
        alert(e instanceof Error ? e.message : 'Failed to load tenant default.');
      } finally {
        setBusyMsg(null);
      }
      return;
    }
    setSnapshot(structuredClone(data));
    setEditScope('user');
  };

  const cancelEdit = async () => {
    const wasAdminScope = editScope === 'admin';
    setSnapshot(null);
    setEditScope(null);
    if (wasAdminScope) {
      setBusyMsg('Restoring your view…');
      try { await load(activeFilters); } finally { setBusyMsg(null); }
    } else if (snapshot) {
      setData(snapshot);
    }
  };

  const commitEdit = async () => {
    if (!data || !snapshot || !editScope) return;
    setBusyMsg('Saving changes…');
    try {
      const wId = (w: Widget) => (w as Widget & { widget_id?: string }).widget_id ?? '';
      const snapWidgetIds = snapshot.widgets.map(wId).filter(Boolean);
      const currWidgetIds = data.widgets.map(wId).filter(Boolean);
      const widgetsChanged =
        snapWidgetIds.length !== currWidgetIds.length ||
        snapWidgetIds.some((id, i) => id !== currWidgetIds[i]);

      const snapTypes = new Map(snapshot.widgets.map(w => [wId(w), w.type]));
      const typeChanges: Array<{ id: string; type: WidgetType }> = [];
      for (const w of data.widgets) {
        const id = wId(w);
        if (id && snapTypes.get(id) && snapTypes.get(id) !== w.type) {
          typeChanges.push({ id, type: w.type });
        }
      }

      // Ops must run sequentially — every endpoint does read-modify-write
      // on the same layout row, so parallel calls lose updates.
      const ops: Array<() => Promise<unknown>> = [];

      if (editScope === 'admin') {
        if (widgetsChanged) ops.push(() => apiAdminSetTenantWidgets(currWidgetIds));
        for (const { id, type } of typeChanges) ops.push(() => apiAdminUpdateTenantWidgetType(id, type));

        const snapCatalog = snapshot.filters_catalog ?? snapshot.filters;
        const currCatalog = data.filters_catalog ?? data.filters;
        const catalogChanged =
          snapCatalog.length !== currCatalog.length ||
          snapCatalog.some((f, i) => f.id !== currCatalog[i]?.id);
        if (catalogChanged) ops.push(() => apiAdminSetTenantFilters(currCatalog.map(f => f.id)));
      } else {
        if (widgetsChanged) {
          const removed = snapWidgetIds.filter(id => !currWidgetIds.includes(id));
          for (const id of removed) ops.push(() => apiRemoveWidget(id));
        }
        for (const { id, type } of typeChanges) ops.push(() => apiUpdateWidgetType(id, type));

        const catalog = data.filters_catalog ?? data.filters;
        const snapCatalog = snapshot.filters_catalog ?? snapshot.filters;
        const snapVisible = snapshot.filters.map(f => f.id);
        const currVisible = data.filters.map(f => f.id);
        const snapHidden = snapCatalog.filter(f => !snapVisible.includes(f.id)).map(f => f.id);
        const currHidden = catalog.filter(f => !currVisible.includes(f.id)).map(f => f.id);

        const hiddenChanged =
          snapHidden.length !== currHidden.length ||
          [...snapHidden].sort().join(',') !== [...currHidden].sort().join(',');
        if (hiddenChanged) ops.push(() => apiUpdateFilterVisibility(currHidden));

        const orderChanged =
          snapVisible.length !== currVisible.length ||
          snapVisible.some((id, i) => id !== currVisible[i]);
        if (orderChanged) ops.push(() => apiUpdateFilterOrder(currVisible));
      }

      for (const op of ops) await op();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to save changes.');
    } finally {
      setSnapshot(null);
      setEditScope(null);
      try { await load(activeFilters); } finally { setBusyMsg(null); }
    }
  };

  const handleRemoveFilter = (idOrAction: string) => {
    if (!data) return;
    const catalog = data.filters_catalog ?? data.filters;

    if (editScope === 'admin') {
      const newCatalog = catalog.filter(f => f.id !== idOrAction);
      setData({ ...data, filters: newCatalog, filters_catalog: newCatalog });
      return;
    }

    if (idOrAction.startsWith('__restore__')) {
      const id = idOrAction.replace('__restore__', '');
      const restored = catalog.find(f => f.id === id);
      if (!restored) return;
      setData({ ...data, filters: [...data.filters, restored] });
      return;
    }

    setData({ ...data, filters: data.filters.filter(f => f.id !== idOrAction) });
  };

  const handleReorder = (orderedIds: string[]) => {
    if (!data) return;
    const idSet = new Set(orderedIds);
    const reordered = [
      ...orderedIds.map(id => data.filters.find(f => f.id === id)!).filter(Boolean),
      ...data.filters.filter(f => !idSet.has(f.id)),
    ];
    if (editScope === 'admin') {
      setData({ ...data, filters: reordered, filters_catalog: reordered });
    } else {
      setData({ ...data, filters: reordered });
    }
  };

  const handleResetFilters = () => {
    if (!data) return;
    const catalog = data.filters_catalog ?? data.filters;
    setData({ ...data, filters: catalog });
  };

  const handleRemoveWidget = (widgetId: string) => {
    if (!data) return;
    const next = data.widgets.filter(w => (w as Widget & { widget_id?: string }).widget_id !== widgetId);
    setData({ ...data, widgets: next });
  };

  const handleChangeWidgetType = (widgetId: string, newType: WidgetType) => {
    if (!data) return;
    const next = data.widgets.map(w =>
      (w as Widget & { widget_id?: string }).widget_id === widgetId ? ({ ...w, type: newType } as Widget) : w
    );
    setData({ ...data, widgets: next });
  };

  const handleWidgetAdded = (widget: Record<string, unknown>) => {
    if (!data) return;
    const w = widget as unknown as Widget;
    setData({ ...data, widgets: [...data.widgets, w] });
    // Include the newly-added widget in the snapshot so Cancel doesn't try to remove it
    if (snapshot) setSnapshot({ ...snapshot, widgets: [...snapshot.widgets, w] });
  };

  // Exports what has already been fetched — no refetch, so the file cannot
  // disagree with the figures on screen.
  const handleExportPdf = async () => {
    if (!data) return;
    setBusyMsg('Preparing PDF…');
    try {
      const { exportDashboardPdf } = await import('@/components/dashboard/exportPdf');
      await exportDashboardPdf({
        widgets: data.widgets,
        filters: data.filters,
        activeFilters,
        periodLabel: period ? PERIOD_LABEL[period] : null,
        narrative,
      });
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Export failed.');
    } finally {
      setBusyMsg(null);
    }
  };

  const handleReset = async () => {
    if (!confirm('Reset to company default? Your custom widgets will be removed.')) return;
    setResetting(true);
    try {
      await apiResetLayout();
      setSnapshot(null);
      setEditScope(null);
      setData(null);
      setLoading(true);
      load({});
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Reset failed.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <SidebarProvider>
      <div className="flex flex-col h-screen w-screen bg-background">
        <Header />

        {busyMsg && (
          <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2.5 px-4 py-2 rounded-full bg-background border border-border shadow-lg text-sm">
            <svg className="w-4 h-4 animate-spin text-primary" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
              <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
            <span className="text-foreground font-medium">{busyMsg}</span>
          </div>
        )}

        <div className="flex-1 overflow-auto px-6 pb-6 pt-0">

          {/* Title row — edit controls only shown when feature is enabled */}
          {!featuresLoading && features.dashboard && (
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-semibold">Dashboard</h1>
              <div className="flex items-center gap-2">
                {editMode ? (
                  <>
                    <button
                      onClick={() => setAddOpen(true)}
                      disabled={busy}
                      className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                    >+ Add Widget</button>
                    {editScope === 'user' && (
                      <>
                        <button
                          onClick={handleResetFilters}
                          disabled={busy}
                          className="px-3 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-50"
                        >Reset Filters</button>
                        <button
                          onClick={handleReset}
                          disabled={resetting || busy}
                          className="px-3 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-50"
                        >{resetting ? 'Resetting…' : 'Reset to Default'}</button>
                      </>
                    )}
                    <button
                      onClick={cancelEdit}
                      disabled={busy}
                      className="px-3 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-50"
                    >Cancel</button>
                    <button
                      onClick={commitEdit}
                      disabled={busy}
                      className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                    >{busyMsg === 'Saving changes…' ? 'Saving…' : 'Done'}</button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleExportPdf}
                      disabled={busy || !data || data.widgets.length === 0}
                      className="px-3 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-50"
                    >Export PDF</button>
                    <button
                      onClick={() => startEdit('user')}
                      className="px-3 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                    >Edit</button>
                    {isAdmin && (
                      <button
                        onClick={() => startEdit('admin')}
                        className="px-3 py-1.5 rounded-lg border border-amber-500/40 text-sm text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 transition-colors"
                      >Edit Default</button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {editScope === 'admin' && (
            <div className="mb-4 px-4 py-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm flex items-center gap-2">
              <span className="font-semibold">Editing company default</span>
              <span className="text-muted-foreground">— changes apply to all users. Click Done to save or Cancel to discard.</span>
            </div>
          )}
          {editScope === 'user' && (
            <div className="mb-4 px-4 py-2.5 rounded-lg border border-primary/30 bg-primary/5 text-primary text-sm flex items-center gap-2">
              <span className="font-semibold">Editing your view</span>
              <span className="text-muted-foreground">— changes apply only to you. Click Done to save or Cancel to discard.</span>
            </div>
          )}

          {/* Above the period chips on purpose: the score is computed nightly
              over a fixed 30-day window and does NOT move with the period
              selected below. Putting it under the chips would imply it does. */}
          {!featuresLoading && features.anomaly_detection && !editMode && (
            <HealthScoreRow />
          )}

          {data && primaryDateFilter(data.filters || []) && (
            <div className="mb-4 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Period</span>
              {PERIOD_ORDER.map((p) => (
                <button
                  key={p}
                  onClick={() => applyPeriod(p)}
                  disabled={busy}
                  className={`text-xs rounded-full border px-3 py-1 transition-colors ${
                    period === p
                      ? 'border-primary bg-primary/10 text-foreground font-medium'
                      : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {PERIOD_LABEL[p]}
                </button>
              ))}
              <span className="text-xs text-muted-foreground ml-1">
                every figure below is for this period, compared with the one before it
              </span>
            </div>
          )}

          {data && !editMode && (
            <Narrative data={narrative} loading={narrLoading} />
          )}

          {data && (data.filters.length > 0 || editMode) && (
            <FilterPanel
              filters={data.filters}
              catalog={data.filters_catalog ?? data.filters}
              active={activeFilters}
              editMode={editMode}
              onApply={applyFilterKey}
              onClearAll={clearAll}
              onRemoveFilter={handleRemoveFilter}
              onReorder={handleReorder}
              hasActive={hasActive}
            />
          )}

          {featuresLoading && <LoadingGrid />}
          {!featuresLoading && !features.dashboard && <FeatureDisabled />}
          {!featuresLoading && features.dashboard && loading && !data && <LoadingGrid />}
          {!featuresLoading && features.dashboard && error && <ErrorState message={error} onRetry={() => load(activeFilters)} />}
          {/* Refreshing with figures already on screen. The skeleton above only
              covers the very first load (`!data`), so changing a filter used to
              show nothing at all: for a few seconds the LAST period's numbers
              sat under the NEW filter chip, looking like the answer to a
              question they had not been asked. Say it is working, and take the
              stale figures out of reading condition while it is. */}
          {!featuresLoading && features.dashboard && loading && data && (
            <div
              role="status"
              aria-live="polite"
              className="flex items-center gap-2 mb-3 text-xs text-muted-foreground"
            >
              <span className="size-3 rounded-full border-2 border-muted-foreground/30 border-t-primary animate-spin" />
              Updating the figures for the filters you just set…
            </div>
          )}
          {/* A tile that timed out used to just not be there, and a dashboard
              with its journal totals missing read as a complete one. */}
          {!featuresLoading && features.dashboard && !loading && data?.incomplete && data.incomplete.length > 0 && (
            <div
              role="status"
              className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3 text-xs text-muted-foreground"
            >
              <span>
                {data.incomplete.length === 1
                  ? '1 tile did not load this time and is not shown'
                  : `${data.incomplete.length} tiles did not load this time and are not shown`}
                : {data.incomplete.join(', ')}.
              </span>
              <button
                type="button"
                onClick={() => load(activeFilters)}
                className="underline underline-offset-2 hover:text-foreground"
              >
                Try again
              </button>
            </div>
          )}
          {data && (() => {
            const financeCount = data.widgets.filter(w => w.group === 'finance').length;
            const execCount = data.widgets.length - financeCount;
            const shown = financeCount === 0 || execCount === 0
              ? data.widgets
              : data.widgets.filter(w => (w.group ?? 'executive') === group);
            const refreshing = loading && !featuresLoading && features.dashboard;
            return (
              <div
                className={refreshing
                  ? 'opacity-40 pointer-events-none transition-opacity motion-reduce:transition-none'
                  : 'transition-opacity motion-reduce:transition-none'}
                aria-busy={refreshing}
              >
                {financeCount > 0 && execCount > 0 && (
                  <div className="flex gap-1 border-b mb-4">
                    {([
                      ['executive', 'Overview', execCount],
                      ['finance', 'Finance detail', financeCount],
                    ] as [WidgetGroup, string, number][]).map(([id, label, count]) => (
                      <button
                        key={id}
                        onClick={() => setGroup(id)}
                        className={`px-3 py-2 text-sm border-b-2 -mb-px ${
                          group === id
                            ? 'border-primary text-foreground font-medium'
                            : 'border-transparent text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {label} <span className="text-xs opacity-70">({count})</span>
                      </button>
                    ))}
                  </div>
                )}
                <WidgetGrid
                  widgets={shown}
                  isDark={isDark}
                  editMode={editMode}
                  onRemove={handleRemoveWidget}
                  onChangeType={handleChangeWidgetType}
                  onDrill={editMode ? undefined : setDrilldown}
                />
              </div>
            );
          })()}
        </div>
      </div>

      <DrilldownPanel
        target={drilldown}
        filters={data?.filters ?? []}
        activeFilters={activeFilters}
        periodLabel={period ? PERIOD_LABEL[period] : null}
        onClose={() => setDrilldown(null)}
      />

      {addOpen && (
        <AddWidgetDialog
          onClose={() => setAddOpen(false)}
          onAdded={handleWidgetAdded}
          scope={editScope === 'admin' ? 'admin' : 'user'}
        />
      )}
    </SidebarProvider>
  );
}


// ── widget grid ──────────────────────────────────────────────────────────────

type WidgetWithId = Widget & { widget_id?: string };

function WidgetGrid({ widgets, isDark, editMode, onRemove, onChangeType, onDrill }: {
  widgets: Widget[];
  isDark: boolean;
  editMode: boolean;
  onRemove: (id: string) => void;
  onChangeType: (id: string, type: WidgetType) => void;
  onDrill?: (target: DrilldownTarget) => void;
}) {
  const allKpis = widgets.filter(w => w.type === 'kpi' || w.type === 'count') as KpiLike[];
  const charts  = widgets.filter(w => w.type !== 'kpi' && w.type !== 'count') as ChartOnly[];

  // Every tile used to be the same size, so a 0 and a 5.3B asked for the eye
  // equally and the page had no place to start. Three bands instead, chosen by
  // the numbers themselves so this holds for any tenant rather than being a
  // hand-picked list for one:
  //
  //   headline  the four largest figures, full size, above the charts
  //   rest      everything else, half size, BELOW the charts
  //   empty     zero or missing, one line at the bottom
  //
  // Charts move above the secondary figures because a shape is read faster than
  // a column of numbers, and burying them under fourteen tiles wasted them.
  // Three bands, and the split comes from the values so it holds for any tenant
  // rather than being a hand-picked list for one.
  const HEADLINE_COUNT = 4;
  const isEmpty = (w: KpiLike) =>
    (w as Partial<Unavailable>).unavailable !== true &&
    (w.value === 0 || w.value === null || w.value === undefined);

  const ranked = allKpis
    .filter(w => !isEmpty(w))
    .slice()
    .sort((a, b) => Math.abs(Number(b.value) || 0) - Math.abs(Number(a.value) || 0));

  const headline = ranked.slice(0, HEADLINE_COUNT);
  const headlineIds = new Set(headline);
  const rest = allKpis.filter(w => !headlineIds.has(w) && !isEmpty(w));
  const empty = allKpis.filter(isEmpty);

  const blockedNote = (w: Widget) => (
    <div
      key={((w as WidgetWithId).widget_id ?? w.title) + '-blocked'}
      className="rounded-xl border bg-card text-card-foreground shadow-sm p-4"
    >
      <div className="text-sm font-medium mb-1">{w.title}</div>
      <div className="text-xs text-amber-700 dark:text-amber-300">
        ⚠️ {(w as Unavailable).reason}
      </div>
      <div className="text-[11px] text-muted-foreground mt-1">
        Reported to your BuduCloud team — the figure returns once the source data is fixed.
      </div>
    </div>
  );

  const kpiCard = (w: KpiLike, compact: boolean) => {
    const id = (w as WidgetWithId).widget_id ?? '';
    if ((w as Partial<Unavailable>).unavailable) return blockedNote(w);
    return (
      <KpiCard
        key={id || w.title}
        widget={w}
        compact={compact}
        editMode={editMode}
        onRemove={() => onRemove(id)}
        onChangeType={(t) => onChangeType(id, t)}
        onDrill={id && onDrill
          ? () => onDrill({ widgetId: id, title: w.title, displayedValue: w.value, label: null })
          : undefined}
      />
    );
  };

  return (
    <>
      {/* The four largest figures, full width. Everything else moves into a rail
          beside the charts: stacking the leftovers underneath turned them into a
          second wall, which is the thing this was meant to stop. A narrow column
          next to the charts keeps them readable and keeps a number within reach
          of the shape that explains it. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {headline.map((w) => kpiCard(w, false))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
        <div className="lg:col-span-3 grid grid-cols-1 xl:grid-cols-2 gap-4">
          {charts.map((w) => {
            const id = (w as WidgetWithId).widget_id ?? '';
            if ((w as Partial<Unavailable>).unavailable) return blockedNote(w);
            return (
              <ChartCard
                key={id || w.title}
                widget={w as LineWidget | BarWidget}
                isDark={isDark}
                editMode={editMode}
                onRemove={() => onRemove(id)}
                onChangeType={(t) => onChangeType(id, t)}
                onDrill={id && onDrill ? onDrill : undefined}
                widgetId={id}
              />
            );
          })}
        </div>

        {rest.length > 0 && (
          /* One column on desktop, a normal grid on narrow screens where a rail
             would be a single file of tiles a mile long. */
          <div className="lg:col-span-1 grid grid-cols-2 lg:grid-cols-1 gap-3 content-start">
            {rest.map((w) => kpiCard(w, true))}
          </div>
        )}
      </div>

      {empty.length > 0 && (
        /* A row of zeros is not a finding, it is furniture. Kept, because a
           figure vanishing without trace is worse, but on one line. */
        <p className="text-xs text-muted-foreground">
          Nothing to show this period:{' '}
          {empty.map((w, i) => (
            <span key={(w as WidgetWithId).widget_id ?? w.title}>
              {i > 0 ? ', ' : ''}
              {w.title}
            </span>
          ))}
          .
        </p>
      )}
    </>
  );
}

function TypePicker({ current, onChange }: { current: WidgetType; onChange: (t: WidgetType) => void }) {
  const options = WIDGET_TYPE_GROUPS[current] ?? [current];
  if (options.length <= 1) return null;
  return (
    <select
      value={current}
      onClick={e => e.stopPropagation()}
      onChange={e => onChange(e.target.value as WidgetType)}
      className="text-xs px-1.5 py-0.5 rounded border border-border bg-background text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
      title="Change how this widget is drawn"
    >
      {options.map(o => <option key={o} value={o}>{WIDGET_TYPE_LABELS[o]}</option>)}
    </select>
  );
}

// Both controls used to float over the card, so the type dropdown sat on top of
// the widget's own title ("KPI ⌄" covering "…bit Amount"). In edit mode the card
// gets a real row of its own instead, and the content below it is untouched.
function EditToolbar({ current, onChangeType, onRemove }: {
  current: WidgetType;
  onChangeType: (t: WidgetType) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-dashed">
      <TypePicker current={current} onChange={onChangeType} />
      <button
        onClick={e => { e.stopPropagation(); onRemove(); }}
        className="ml-auto text-xs text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
        title="Remove this widget"
      >
        <span className="text-sm leading-none">×</span> Remove
      </button>
    </div>
  );
}

// ── kpi card ─────────────────────────────────────────────────────────────────

export function formatCompact(v: number): string {
  return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(v);
}

function KpiCard({ widget, editMode, onRemove, onChangeType, onDrill, compact }: { widget: KpiLike; editMode: boolean; onRemove: () => void; onChangeType: (t: WidgetType) => void; onDrill?: () => void; compact?: boolean }) {
  return (
    <div
      className={`relative rounded-lg border bg-card p-4 ${onDrill ? 'cursor-pointer hover:border-primary/50 transition-colors' : ''}`}
      onClick={onDrill}
      title={onDrill ? 'See the rows behind this number' : undefined}
    >
      {editMode && (
        <EditToolbar current={widget.type} onChangeType={onChangeType} onRemove={onRemove} />
      )}
      <p className={`text-muted-foreground truncate ${compact ? 'text-xs' : 'text-sm'}`}>
        {widget.title}
      </p>
      <p
        className={`font-bold mt-1 ${compact ? 'text-xl' : 'text-3xl'}`}
        title={widget.value.toLocaleString()}
      >
        {formatCompact(widget.value)}
        {widget.unit && <span className="text-base font-normal ml-1">{widget.unit}</span>}
      </p>
      {widget.delta_pct != null && widget.direction && widget.direction !== 'none' ? (
        <p
          className={`text-xs mt-1 ${
            widget.direction === 'up'
              ? 'text-emerald-600 dark:text-emerald-400'
              : widget.direction === 'down'
                ? 'text-red-600 dark:text-red-400'
                : 'text-muted-foreground'
          }`}
          title={`Previous period: ${(widget.previous ?? 0).toLocaleString()}`}
        >
          {widget.direction === 'up' ? '▲' : widget.direction === 'down' ? '▼' : '◆'}{' '}
          {Math.abs(widget.delta_pct).toFixed(1)}% vs previous period
        </p>
      ) : (
        <p className="text-xs mt-1 text-muted-foreground">
          {widget.previous === undefined ? 'pick a period to compare' : 'no comparable period'}
        </p>
      )}
    </div>
  );
}

// ── chart card ───────────────────────────────────────────────────────────────

/** The clicked datapoint's label and value, or null when the click missed. */
function pointAt(widget: ChartOnly, seriesIndex: number, dataPointIndex: number) {
  // A donut's series is a flat number[], so its category is the series index;
  // every other type here is single-series and indexes by datapoint.
  const i = widget.type === 'pie' ? seriesIndex : dataPointIndex;
  if (i == null || i < 0) return null;
  if (widget.type === 'line' || widget.type === 'area') {
    if (i >= widget.x.length) return null;
    return { label: widget.x[i], value: widget.y[i] };
  }
  const w = widget as BarWidget;
  if (i >= w.labels.length) return null;
  return { label: w.labels[i], value: w.values[i] };
}

function ChartCard({ widget, isDark, editMode, onRemove, onChangeType, onDrill, widgetId }: { widget: ChartOnly; isDark: boolean; editMode: boolean; onRemove: () => void; onChangeType: (t: WidgetType) => void; onDrill?: (target: DrilldownTarget) => void; widgetId?: string }) {
  const opts = buildApexOptions(widget, isDark);
  const series = buildApexSeries(widget);
  const isPie  = widget.type === 'pie';
  const isTopN = widget.type === 'top_n';

  // ApexCharts binds event handlers from the options it was constructed with
  // and does not refresh them on update, so the handler reads through a ref
  // rather than closing over this render's props.
  const live = useRef({ widget, onDrill, widgetId });
  live.current = { widget, onDrill, widgetId };

  const chartOptions: ApexCharts.ApexOptions = onDrill
    ? {
        ...opts,
        chart: {
          ...opts.chart,
          events: {
            dataPointSelection: (
              _e: MouseEvent,
              _ctx?: unknown,
              cfg?: { seriesIndex: number; dataPointIndex: number }
            ) => {
              const { widget: w, onDrill: drill, widgetId: id } = live.current;
              if (!drill || !id || !cfg) return;
              const pt = pointAt(w, cfg.seriesIndex, cfg.dataPointIndex);
              if (!pt) return;
              drill({ widgetId: id, title: w.title, displayedValue: pt.value, label: pt.label });
            },
          },
        },
        // Re-clicking a selected point should reopen the panel, not sit in a
        // toggled-off state that looks like the click did nothing.
        states: { active: { allowMultipleDataPointsSelection: false } },
      }
    : opts;

  return (
    <div className="relative rounded-lg border bg-card p-4">
      {editMode && (
        <EditToolbar current={widget.type} onChangeType={onChangeType} onRemove={onRemove} />
      )}
      <p className="text-sm font-medium mb-1">{widget.title}</p>
      <Suspense fallback={<div className="h-48 flex items-center justify-center text-muted-foreground text-sm">Loading chart…</div>}>
        <ReactApexChart
          key={widget.type}
          type={opts.chart!.type as never}
          series={series}
          options={chartOptions}
          height={isPie ? 240 : isTopN ? Math.max(180, (widget as TopNWidget).labels.length * 28) : 210}
        />
      </Suspense>
    </div>
  );
}

export function buildApexSeries(widget: ChartOnly): ApexCharts.ApexOptions['series'] {
  switch (widget.type) {
    case 'line':
    case 'area':
      return [{ name: widget.title, data: widget.y }];
    case 'bar':
    case 'count_by':
      return [{ name: widget.title, data: (widget as BarWidget).values }];
    case 'top_n':
      return [{ name: widget.title, data: widget.values }];
    case 'pie':
      return widget.values;
  }
}

export function buildApexOptions(widget: ChartOnly, isDark: boolean): ApexCharts.ApexOptions {
  const base = baseOptions(isDark);

  switch (widget.type) {
    case 'line':
      return {
        ...base,
        chart: { ...base.chart, type: 'line' },
        stroke: { curve: 'smooth', width: 2 },
        markers: { size: 3 },
        xaxis: { ...base.xaxis, categories: widget.x },
      };

    case 'area':
      return {
        ...base,
        chart: { ...base.chart, type: 'area' },
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.02 } },
        xaxis: { ...base.xaxis, categories: widget.x },
      };

    case 'bar':
    case 'count_by':
      return {
        ...base,
        chart: { ...base.chart, type: 'bar' },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
        xaxis: { ...base.xaxis, categories: (widget as BarWidget).labels },
      };

    case 'top_n':
      return {
        ...base,
        chart: { ...base.chart, type: 'bar' },
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%' } },
        xaxis: { ...base.xaxis, categories: widget.labels, labels: { ...base.xaxis!.labels, formatter: (v: number) => formatCompact(v) } },
        yaxis: { labels: { style: { colors: isDark ? '#94a3b8' : '#64748b', fontSize: '11px' } } },
      };

    case 'pie':
      return {
        ...base,
        chart: { ...base.chart, type: 'donut' },
        labels: widget.labels,
        plotOptions: { pie: { donut: { size: '60%' } } },
        legend: { ...base.legend, position: 'bottom' },
        dataLabels: { enabled: true, formatter: (_val: number, opts: { w: { globals: { series: number[] } }; seriesIndex: number }) => {
          const total = opts.w.globals.series.reduce((a: number, b: number) => a + b, 0);
          const pct = ((opts.w.globals.series[opts.seriesIndex] / total) * 100).toFixed(1);
          return `${pct}%`;
        }},
      };
  }
}

// ── loading / error ───────────────────────────────────────────────────────────

function LoadingGrid() {
  return (
    <div className="space-y-6">
      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="h-4 w-12 rounded bg-muted animate-pulse" />
        {[80, 96, 112, 88, 104, 72, 96, 80].map((w, i) => (
          <div key={i} className="h-7 rounded-full bg-muted animate-pulse" style={{ width: w }} />
        ))}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="rounded-lg border bg-card p-4 space-y-3">
            <div className="h-3 w-2/3 rounded bg-muted animate-pulse" />
            <div className="h-8 w-1/2 rounded bg-muted animate-pulse" />
          </div>
        ))}
      </div>

      {/* Chart rows */}
      {[...Array(3)].map((_, row) => (
        <div key={row} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(2)].map((_, col) => (
            <div key={col} className="rounded-lg border bg-card p-4 space-y-3">
              <div className="h-3 w-1/3 rounded bg-muted animate-pulse" />
              <div className="h-48 rounded bg-muted animate-pulse" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center h-64 gap-3 text-muted-foreground">
      <p>Failed to load dashboard: {message}</p>
      <button onClick={onRetry} className="px-4 py-2 rounded border hover:bg-accent">Retry</button>
    </div>
  );
}

function FeatureDisabled() {
  return (
    <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
      <p className="text-lg font-medium">Dashboard not available</p>
      <p className="text-sm text-muted-foreground">This feature is not enabled for your account.<br />Contact your administrator to request access.</p>
    </div>
  );
}
